import {useRef} from 'react';

import {browserHistory} from 'react-router';

import {useGeneratePolicyContent} from 'app/policies/api';
import {useGenerateProcedureContent} from 'app/procedures/api';
import {getPathFromURL} from 'app/utils/urls';

import {SuggestionContentType, SuggestionItem} from './types';

import type {FormErrorsProps} from 'app/general/forms';
import type {CreatableDocType} from 'app/general/types';
import type {UserJobSerializedType} from 'sweet-types';

interface Props
  extends Partial<Pick<FormErrorsProps, 'captureFormErrorsFromRejection'>> {
  createPromise: Promise<CreatableDocType>;
  generateContent:
    | ReturnType<typeof useGeneratePolicyContent>
    | ReturnType<typeof useGenerateProcedureContent>;
  onSuccess: (job: UserJobSerializedType, element: unknown) => unknown;
  onError?: (url: string) => unknown;
}

export function generateNewDocumentContent({
  createPromise,
  generateContent,
  onSuccess,
  onError,
  captureFormErrorsFromRejection,
}: Props) {
  function goToEditUrl(url) {
    browserHistory.push(`${getPathFromURL(url)}edit/`);
  }

  return createPromise.then(
    (element) =>
      generateContent[0]({
        url: element.current_version.url,
        prompt: element.name,
      })
        .unwrap()
        .then(
          (jobResults: UserJobSerializedType) => onSuccess(jobResults, element),
          onError != null
            ? onError
            : () => goToEditUrl(element.current_version.html_url)
        ),
    (err) => {
      if (captureFormErrorsFromRejection != null) {
        captureFormErrorsFromRejection(err);
      }
      throw err;
    }
  );
}

export function extractSuggestionsOfType(
  suggestions: Array<SuggestionItem>,
  contentType: SuggestionContentType
) {
  return suggestions
    .filter((item) => item.content_type === contentType)
    .map((item) => item.name);
}

export const cleanStepId = (stepId?: string): string =>
  (stepId ?? '').replace(/stepid-/, '');

export function useHistoryData<T>(
  initialValue?: T
): [Array<T>, (newEntry: T) => void] {
  const history = useRef<Array<T>>([]);

  const appendHistory = (newEntry: T) => {
    history.current.unshift(newEntry);
  };

  if (initialValue != null) {
    appendHistory(initialValue);
  }

  return [history.current, appendHistory];
}
